// Pure editing operations for the Schedule editor (SchedulerPage.jsx).
//
// The editor state is a FLAT list of rows in visual order; nesting is
// only `indent` (a row's parent is the nearest row above it with a smaller
// indent). Everything here works on that list and never touches a row's
// `key` - predecessor links point at keys, so moving a block keeps every
// link intact.
//
// A "block" is a row plus its whole subtree: the row and every following
// row with a greater indent.
import { normalizeType } from "../../helpers/schedule/activityTypes";

export const indentOf = (r) => Number(r && r.indent) || 0;

// Index one past the end of the block starting at idx.
export function subtreeEnd(rows, idx) {
  const base = indentOf(rows[idx]);
  let j = idx + 1;
  while (j < rows.length && indentOf(rows[j]) > base) j++;
  return j;
}

export const hasChildrenAt = (rows, idx) =>
  idx >= 0 && idx + 1 < rows.length && indentOf(rows[idx + 1]) > indentOf(rows[idx]);

// The top-level blocks covering a set of keys, as [start, end) ranges in
// order. A selected row inside another selected row's block rides along
// with that block rather than being moved twice.
export function blocksFor(rows, keys) {
  const want = keys instanceof Set ? keys : new Set(keys || []);
  const blocks = [];
  let i = 0;
  while (i < rows.length) {
    if (want.has(rows[i].key)) {
      const e = subtreeEnd(rows, i);
      blocks.push([i, e]);
      i = e;
    } else i += 1;
  }
  return blocks;
}

function extractBlocks(rows, keys) {
  const blocks = blocksFor(rows, keys);
  const moving = [];
  const movedKeys = new Set();
  const inBlock = new Uint8Array(rows.length);
  for (const [s, e] of blocks) {
    moving.push(rows.slice(s, e));
    for (let k = s; k < e; k++) { inBlock[k] = 1; movedKeys.add(rows[k].key); }
  }
  const rest = rows.filter((_, i) => !inBlock[i]);
  return { moving, rest, movedKeys };
}

// Re-base a block so its root sits at `indent`, descendants keeping their
// relative depth.
export function rebaseBlock(block, indent) {
  if (!block.length) return block;
  const d = indent - indentOf(block[0]);
  if (!d) return block;
  return block.map((r) => ({ ...r, indent: Math.max(0, indentOf(r) + d) }));
}

// Move the blocks of `keys` to be the LAST children of `sectionKey`.
// Returns { rows, moved } or { error }.
export function moveToSection(rows, keys, sectionKey) {
  if (!keys || !(keys.size || keys.length)) return { error: "Nothing selected to move" };
  const { moving, rest, movedKeys } = extractBlocks(rows, keys);
  if (!moving.length) return { error: "Nothing selected to move" };
  if (movedKeys.has(sectionKey)) return { error: "A row cannot be moved into itself or its own subtree" };
  const si = rest.findIndex((r) => r.key === sectionKey);
  if (si < 0) return { error: "That section is not in the schedule" };
  const at = subtreeEnd(rest, si);
  const indent = indentOf(rest[si]) + 1;
  const insert = [].concat(...moving.map((b) => rebaseBlock(b, indent)));
  return { rows: [...rest.slice(0, at), ...insert, ...rest.slice(at)], moved: insert.length };
}

// Move the blocks of `keys` directly above `targetKey`, or directly after
// the target's own block ("below"), at the target's indent.
export function moveRelative(rows, keys, targetKey, where) {
  if (!keys || !(keys.size || keys.length)) return { error: "Nothing selected to move" };
  const { moving, rest, movedKeys } = extractBlocks(rows, keys);
  if (!moving.length) return { error: "Nothing selected to move" };
  if (movedKeys.has(targetKey)) return { error: "A row cannot be placed relative to itself or a row inside its own subtree" };
  const ti = rest.findIndex((r) => r.key === targetKey);
  if (ti < 0) return { error: "That row is not in the schedule" };
  const at = where === "above" ? ti : subtreeEnd(rest, ti);
  const indent = indentOf(rest[ti]);
  const insert = [].concat(...moving.map((b) => rebaseBlock(b, indent)));
  return { rows: [...rest.slice(0, at), ...insert, ...rest.slice(at)], moved: insert.length };
}

// Up / down: swap the row's block with the neighbouring SIBLING block.
// With no sibling in that direction a single leaf still swaps with the
// adjacent row (the old behaviour, which lets a row step out of a
// section); a block with children does not - use Move to section.
export function moveStep(rows, key, dir) {
  const i = rows.findIndex((r) => r.key === key);
  if (i < 0) return rows;
  const n = rows.length;
  const e = subtreeEnd(rows, i);
  const ind = indentOf(rows[i]);
  const leaf = e === i + 1;
  if (dir < 0) {
    let j = i - 1;
    while (j >= 0 && indentOf(rows[j]) > ind) j -= 1;
    if (j >= 0 && indentOf(rows[j]) === ind) {
      return [...rows.slice(0, j), ...rows.slice(i, e), ...rows.slice(j, i), ...rows.slice(e)];
    }
    if (leaf && i > 0) {
      const next = rows.slice();
      [next[i - 1], next[i]] = [next[i], next[i - 1]];
      return next;
    }
    return rows;
  }
  if (e < n && indentOf(rows[e]) === ind) {
    const e2 = subtreeEnd(rows, e);
    return [...rows.slice(0, i), ...rows.slice(e, e2), ...rows.slice(i, e), ...rows.slice(e2)];
  }
  if (leaf && i + 1 < n) {
    const next = rows.slice();
    [next[i + 1], next[i]] = [next[i], next[i + 1]];
    return next;
  }
  return rows;
}

// CAN THIS ROW MOVE, AND IF NOT, WHY.
//
// Owner, 2026-09-28: "allow when a parent activity is moved, up or down the
// children also follow". IT ALREADY DOES - moveStep takes the whole subtree
// and the test above proves a parent carries its child. WHAT HE COULD NOT DO
// IS PRESS THE BUTTON AND SEE ANYTHING HAPPEN, and his verb is the evidence:
// he wrote "allow", not "fix", which is the word of somebody who believes a
// capability is missing rather than broken.
//
// THE TWO HALVES DISAGREED. The Up and Down buttons were disabled only on the
// FIRST AND LAST ROW OF THE WHOLE LIST, while moveStep refuses whenever there
// is no SIBLING AT THE SAME INDENT in that direction. So on a 628-row
// schedule the button is enabled almost everywhere, and a parent at the top
// of its section is refused in silence: nothing moves, nothing is said, and
// the only available conclusion is that the product cannot do it.
//
// `can` IS DERIVED FROM moveStep ITSELF - it returns the SAME ARRAY when it
// refuses - so the button and the move cannot drift apart. Only the wording
// is computed here; the decision is not re-implemented.
export function canMoveStep(rows, key, dir) {
  const i = rows.findIndex((r) => r.key === key);
  if (i < 0) return { can: false, why: "" };
  if (moveStep(rows, key, dir) !== rows) return { can: true, why: "" };
  const ind = indentOf(rows[i]);
  const first = i === 0;
  const last = i === rows.length - 1;
  if (dir < 0 && first) return { can: false, why: "This is the first activity in the plan." };
  if (dir > 0 && last) return { can: false, why: "This is the last activity in the plan." };
  const where = dir < 0 ? "above" : "below";
  return {
    can: false,
    why: ind === 0
      ? `Nothing ${where} it is at the same level, so there is no place to move it to.`
      : `It is the ${dir < 0 ? "first" : "last"} activity under its parent - outdent it, or move its parent instead.`,
  };
}

// SPLIT A PARENT'S PERIOD ACROSS ITS DIRECT CHILDREN.
//
// Owner, 2026-09-28, and the boundary rule is his, not a scheduler's:
// "if a parent activity has 3 children, the first child takes 1/3 perdiod,
// starting same day as parent, second child takes 1/3 starting at end date
// of first child, and third child takes 1/3 starting last day of second
// child, and ending on the parent final day".
//
// SO THE CHILDREN SHARE THEIR BOUNDARY DAYS. Child 2 starts ON child 1's
// end date, not the day after. THE OBVIOUS IMPLEMENTATION IS THE WRONG ONE:
// `next.start = prev.end + 1` is what a scheduler normally means, it reads
// as correct, and it is not what he asked for. It is written this way on
// purpose and the sentence above is the reason.
//
// CALENDAR DAYS, by his later answer - plain arithmetic thirds, weekends
// included, and a child may start on a Sunday.
//
// FIXED ENDS, COMPUTED MIDDLE: child 1 starts the parent's first day and
// the LAST child ends on the parent's final day, so only the interior
// boundaries are arithmetic. That is also what stops rounding drifting the
// end of the plan.
//
// DIRECT CHILDREN ONLY. A grandchild is never touched by an action taken on
// its grandparent - a person who split one parent would not expect rows two
// levels down to move.
//
// THE REMAINDER GOES TO THE LAST CHILD, which is the only choice that keeps
// his fixed end: rounding each child independently leaves the last one short
// of the parent's final day, which is the quiet failure.
const MS_DAY = 86400000;
const dayStart = (iso) => { const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`); return Number.isNaN(+d) ? null : d; };
const isoOf = (d) => new Date(d).toISOString().slice(0, 10);

/**
 * @returns {{ok:boolean, why?:string, segments?:Array<{key:string,start:string,end:string,days:number}>}}
 */
export function splitPeriod(rows, parentKey, { start, end } = {}) {
  const i = rows.findIndex((r) => r.key === parentKey);
  if (i < 0) return { ok: false, why: "That row is not in the plan." };
  const base = indentOf(rows[i]);
  const e = subtreeEnd(rows, i);
  const kids = [];
  for (let j = i + 1; j < e; j++) if (indentOf(rows[j]) === base + 1) kids.push(rows[j]);
  if (!kids.length) return { ok: false, why: "This activity has no children to split its period between." };

  const s0 = dayStart(start);
  const s1 = dayStart(end);
  if (!s0 || !s1) return { ok: false, why: "This activity has no start and finish to divide." };
  const span = Math.round((s1 - s0) / MS_DAY);
  if (span <= 0) return { ok: false, why: "Its finish is not after its start, so there is no period to divide." };
  if (kids.length === 1) {
    return { ok: true, segments: [{ key: kids[0].key, start: isoOf(s0), end: isoOf(s1), days: span }] };
  }
  if (span < kids.length) {
    return { ok: false, why: `${span} day${span === 1 ? "" : "s"} cannot be divided between ${kids.length} children - each needs at least one.` };
  }

  const per = Math.floor(span / kids.length);
  const segments = [];
  let cursor = 0;
  for (let k = 0; k < kids.length; k++) {
    const from = cursor;
    // THE LAST ONE ENDS ON THE PARENT'S FINAL DAY, remainder and all.
    const to = k === kids.length - 1 ? span : cursor + per;
    segments.push({
      key: kids[k].key,
      start: isoOf(new Date(+s0 + from * MS_DAY)),
      end: isoOf(new Date(+s0 + to * MS_DAY)),
      days: to - from,
    });
    // SHARED BOUNDARY: the next child starts where this one ended.
    cursor = to;
  }
  return { ok: true, segments };
}

// ── collapse ────────────────────────────────────────────────────────────

// The rows left after hiding everything under a collapsed row.
export function visibleUnderCollapse(rows, collapsed) {
  if (!collapsed || !collapsed.size) return rows;
  const out = [];
  let hideIndent = null;
  for (const r of rows) {
    const ind = indentOf(r);
    if (hideIndent !== null) {
      if (ind > hideIndent) continue;
      hideIndent = null;
    }
    out.push(r);
    if (collapsed.has(r.key)) hideIndent = ind;
  }
  return out;
}

// Rows with children at indent >= level: collapsing those shows indents
// 0..level. level 0 = heads only.
export function collapsedForLevel(rows, level) {
  const out = new Set();
  rows.forEach((r, i) => { if (indentOf(r) >= level && hasChildrenAt(rows, i)) out.add(r.key); });
  return out;
}

// Keys of the row and every row in its subtree (for "expand all below").
export function subtreeKeys(rows, key) {
  const i = rows.findIndex((r) => r.key === key);
  if (i < 0) return [];
  return rows.slice(i, subtreeEnd(rows, i)).map((r) => r.key);
}

// CLONE A SUBTREE - a parent and every one of its children (owner,
// 2026-10-02: "I urgently need a clone to a full structure in the
// schedule, parents and their children, then edit every field").
//
// He is building the same Level/Area/Discipline shape by hand, over and
// over. So this copies the SHAPE and nothing else.
//
// WHY THERE IS NO parentId REMAPPING HERE, which is the first thing you
// would expect to need: nesting in this table is INDENT PLUS ORDER, and
// `parentId` is derived from that at save time (SchedulerPage's pass 2).
// Copy the block with its indents intact and drop it in after the source
// and the children point at the copied parent by construction. Remapping
// ids by hand would be a second, disagreeing source of truth.
//
// WHAT MUST NOT TRAVEL, and the reason is one sentence: he is cloning a
// shape, not a history. If progress, status, the sign-off RECORD or the
// field counts came with it, a brand-new activity would report itself
// complete the moment it appeared. `_id` goes too, or the save would
// PATCH the original instead of creating a row; so do `_loadedStart` /
// `_loadedEnd`, which exist only to tell a loaded row from an edited one.
//
// PLANNED signOffDate DOES travel, and that is deliberate: "this activity
// needs a sign-off" is part of the shape being copied. `signedOffAt` /
// `signedOffBy` - the record that one HAPPENED - do not.
//
// PREDECESSORS DO NOT TRAVEL EITHER. They are the keys of other rows, so
// a copy would either follow the original's neighbours (logic nobody
// asked for) or need a remap whose right answer is not knowable here.
// An empty list is the honest shape: the copy is unsequenced until
// somebody sequences it.
//
// "(copy)" GOES ON THE TOP ROW ONLY. Naming 600 descendants "X (copy)"
// renames his whole structure; the parent is what he will rename anyway.
export function cloneSubtree(rows, key, { makeKey, suffix = " (copy)" } = {}) {
  const i = (rows || []).findIndex((r) => r.key === key);
  if (i < 0) return { rows, addedKeys: [] };
  if (typeof makeKey !== "function") throw new Error("cloneSubtree needs makeKey");
  const end = subtreeEnd(rows, i);
  const block = rows.slice(i, end);
  const addedKeys = [];
  const copies = block.map((r, n) => {
    const k = makeKey();
    addedKeys.push(k);
    const {
      _id, _loadedStart, _loadedEnd,
      signedOffAt, signedOffBy, gate, earlyStart,
      ...shape
    } = r;
    return {
      ...shape,
      key: k,
      name: n === 0 ? `${r.name || ""}${suffix}` : (r.name || ""),
      predecessors: [],
      status: "planned",
      progress: 0,
      trackingProgress: null,
      trackingProgressCount: 0,
      signedOffAt: null,
      signedOffBy: null,
      gate: null,
      earlyStart: null,
    };
  });
  const next = [...rows.slice(0, end), ...copies, ...rows.slice(end)];
  return { rows: next, addedKeys };
}

// Keys of every ancestor of a row, nearest first.
export function ancestorKeys(rows, key) {
  const i = rows.findIndex((r) => r.key === key);
  if (i < 0) return [];
  const out = [];
  let ind = indentOf(rows[i]);
  for (let j = i - 1; j >= 0 && ind > 0; j--) {
    if (indentOf(rows[j]) < ind) { out.push(rows[j].key); ind = indentOf(rows[j]); }
  }
  return out;
}

// ── dates ───────────────────────────────────────────────────────────────

const isoUtc = (d) => d.toISOString().slice(0, 10);

// Shift an ISO day by n working days (Mon-Fri, skipping `holidays`, a
// Set / array of ISO days). The engine still snaps to the row's own
// calendar afterwards.
export function shiftWorkingDays(iso, n, holidays) {
  const k = Math.round(Number(n) || 0);
  if (!iso || !k) return iso;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
  if (isNaN(+d)) return iso;
  const hol = holidays instanceof Set ? holidays : new Set(holidays || []);
  const step = k > 0 ? 1 : -1;
  let left = Math.abs(k);
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + step);
    const w = d.getUTCDay();
    if (w !== 0 && w !== 6 && !hol.has(isoUtc(d))) left -= 1;
  }
  return isoUtc(d);
}

// ── save diff ───────────────────────────────────────────────────────────

export const dayOf = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(+d) ? "" : d.toISOString().slice(0, 10);
};
const idOf = (v) => (v === null || v === undefined || v === "" ? "" : String(v));

// Sort keys that keep the SAVED order where they can. Given each row's
// previously stored sortIndex (null for a new row), the longest strictly
// increasing run of stored values is kept as it is and only the rows
// around it get new values fitted into the gaps - so moving one block
// rewrites the moved rows, not every row after them.
export function assignSortIndexes(prev) {
  const n = prev.length;
  const val = prev.map((v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v)));
  // Longest strictly increasing subsequence over the non-null values.
  const tails = []; // indices into val
  const parent = new Array(n).fill(-1);
  for (let i = 0; i < n; i++) {
    if (val[i] === null) continue;
    let lo = 0, hi = tails.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (val[tails[mid]] < val[i]) lo = mid + 1; else hi = mid;
    }
    if (lo > 0) parent[i] = tails[lo - 1];
    tails[lo] = i;
  }
  const kept = new Uint8Array(n);
  for (let k = tails.length ? tails[tails.length - 1] : -1; k >= 0; k = parent[k]) kept[k] = 1;

  const out = new Array(n);
  const renumber = () => Array.from({ length: n }, (_, i) => i + 1);
  let i = 0;
  let lo = null;
  while (i < n) {
    if (kept[i]) { out[i] = val[i]; lo = val[i]; i += 1; continue; }
    let j = i;
    while (j < n && !kept[j]) j += 1;
    const cnt = j - i;
    const hi = j < n ? val[j] : null;
    for (let t = 0; t < cnt; t++) {
      let v;
      if (lo === null && hi === null) v = t + 1;
      else if (hi === null) v = lo + t + 1;
      else if (lo === null) v = hi - cnt + t;
      else if (hi - lo - 1 >= cnt) v = lo + t + 1;
      else {
        const step = (hi - lo) / (cnt + 1);
        if (!(step > 1e-7)) return renumber();
        v = lo + step * (t + 1);
      }
      out[i + t] = v;
    }
    i = j;
  }
  return out;
}

// What a saved activity holds, in the shape Save's PATCH body uses, so
// the two can be compared field by field.
export function snapshotFromItem(item) {
  const it = item || {};
  const t = normalizeType(it.type, it.kind);
  const users = it.responsibleUsers && it.responsibleUsers.length
    ? it.responsibleUsers.map((u) => ({ userId: String(u.userId), name: u.name || "" }))
    : (it.responsibleUserId ? [{ userId: String(it.responsibleUserId), name: it.responsibleUserName || "" }] : []);
  return {
    name: it.name || "",
    type: t.type,
    startDate: dayOf(it.startDate),
    endDate: dayOf(it.endDate || it.startDate),
    wbs: it.wbs || "",
    kind: t.kind || it.milestoneKind || "",
    category: it.category || "",
    stage: it.stage || "",
    discipline: it.discipline || "",
    areaId: it.areaId ? String(it.areaId) : null,
    calendarId: it.calendarId ? String(it.calendarId) : null,
    areaKey: it.areaKey || "",
    level: it.level || "",
    grid: it.grid || "",
    responsibleCompanyId: it.responsibleCompanyId ? String(it.responsibleCompanyId) : "",
    responsibleUsers: users,
    contractorLabel: it.contractorLabel || "",
    disabled: !!it.disabled,
    signOffDate: dayOf(it.signOffDate) || null,
    phase: it.phase || "",
    step: it.step || "",
    workPackage: it.workPackage || "",
    status: it.status || "planned",
    progress: Number(it.progress) || 0,
    parentId: it.parentId ? String(it.parentId) : null,
    dependencies: it.dependencies || [],
    sortIndex: Number.isFinite(Number(it.sortIndex)) ? Number(it.sortIndex) : 0,
  };
}

const depCanon = (deps) => (deps || [])
  .map((d) => {
    const id = d && d.predecessorId !== undefined && d.predecessorId !== null ? d.predecessorId : (d && d._id !== undefined ? d._id : d);
    return `${String(id)}:${String((d && d.type) || "FS").toUpperCase()}:${Number(d && d.lagDays) || 0}`;
  })
  .sort()
  .join("|");

const CANON = {
  name: (v) => String(v == null ? "" : v).trim(),
  type: (v) => String(v || "task"),
  status: (v) => String(v || "planned"),
  startDate: dayOf, endDate: dayOf, signOffDate: dayOf,
  parentId: idOf, areaId: idOf, calendarId: idOf, responsibleCompanyId: idOf,
  dependencies: depCanon,
  responsibleUsers: (us) => (us || []).map((u) => `${String(u.userId != null ? u.userId : (u.value != null ? u.value : ""))}=${u.name || ""}`).join("|"),
  sortIndex: (v) => (Number.isFinite(Number(v)) ? Number(v) : 0),
  progress: (v) => Number(v) || 0,
  disabled: (v) => !!v,
  // The server lower-cases a step and upper-cases a package.
  step: (v) => String(v || "").toLowerCase().slice(0, 40),
  workPackage: (v) => String(v || "").toUpperCase().slice(0, 60),
  recurrence: (v) => (v ? JSON.stringify(v) : ""),
};
const canon = (k, v) => (CANON[k] ? CANON[k](v) : (v === null || v === undefined ? "" : String(v)));

// The fields of `patch` whose value differs from the snapshot. No
// snapshot (a row created this save) = everything.
export function changedFields(patch, snap) {
  const keys = Object.keys(patch || {});
  if (!snap) return keys;
  return keys.filter((k) => canon(k, patch[k]) !== canon(k, snap[k]));
}

// Run `worker` over `items` with at most `limit` in flight. Never rejects:
// each result is { ok, value } or { ok: false, error }, in input order.
export async function runPool(items, limit, worker, onProgress) {
  const list = items || [];
  const results = new Array(list.length);
  let next = 0;
  let done = 0;
  const lane = async () => {
    for (;;) {
      const i = next;
      next += 1;
      if (i >= list.length) return;
      try {
        results[i] = { ok: true, value: await worker(list[i], i) };
      } catch (error) {
        results[i] = { ok: false, error };
      }
      done += 1;
      if (onProgress) onProgress(done, list.length);
    }
  };
  const lanes = Math.max(1, Math.min(Number(limit) || 1, list.length));
  await Promise.all(Array.from({ length: lanes }, lane));
  return results;
}

// ── redistribute ────────────────────────────────────────────────────────

// Every working day from `startIso` to `endIso` inclusive.
//
// Built as a LIST rather than computed by arithmetic because the question
// "which day is the 7th working day" has to agree exactly with "how many
// working days are there", and two separate calculations of that drift.
// One list answers both.
function workingDays(startIso, endIso, holidays) {
  const out = [];
  const a = dayOf(startIso); const b = dayOf(endIso);
  if (!a || !b || a > b) return out;
  const hol = holidays instanceof Set ? holidays : new Set(holidays || []);
  const d = new Date(`${a}T00:00:00Z`);
  const last = new Date(`${b}T00:00:00Z`);
  while (+d <= +last) {
    const w = d.getUTCDay();
    const iso = isoUtc(d);
    if (w !== 0 && w !== 6 && !hol.has(iso)) out.push(iso);
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/**
 * REDISTRIBUTE: divide a row's period equally between its children, in
 * working days, and let each child do the same with its own children,
 * down to the leaves.
 *
 * Owner, 2026-10-02: "add a button that reorganizes schedule
 * automatically, when clicked we are able to override the parent finish
 * time even if its earlier than last activitieis then, click button
 * redestribute, and recursively will split the time equally between all
 * children and their children recursively."
 *
 * THIS DELIBERATELY OVERRIDES A RULE HE SET LAST MONTH - that a parent
 * cannot finish before its latest child - and that floor STAYS for
 * ordinary edits. This is the escape hatch, not its removal: a parent
 * still cannot be dragged under its children by accident, only through
 * this flow, on purpose.
 *
 * EQUAL MEANS WORKING DAYS (his answer, asked as a straight fork):
 * weekends and holidays are skipped, consistent with the Gantt's own
 * barring, and this reuses the same day arithmetic rather than keeping a
 * second calendar.
 *
 * RECURSION IS PER LEVEL, not flat: the row's span is divided between its
 * DIRECT children; each child then divides its new span between its own.
 * A flat split across all descendants would give a leaf the same slice as
 * a summary holding nine of them.
 *
 * LINKS ARE KEPT AND DATES WIN (his answer): predecessors stay on the
 * rows, so a later move re-applies them, and a linked chain can read out
 * of sequence immediately after this. He chose that knowingly.
 *
 * IT CHANGES NOTHING ITSELF. It returns the moves and the refusals so a
 * caller can show them before committing - this rewrites every date under
 * a row on a live schedule in one click, and the same person lost 615
 * rows today to an action that did exactly what it said.
 */
export function redistribute(rows, key, { holidays = [], start = null, end = null } = {}) {
  const list = rows || [];
  const i = list.findIndex((r) => r.key === key);
  if (i < 0) return { ok: false, why: "That row is not in the plan.", changes: [], refused: [] };

  const changes = [];
  const refused = [];
  let disabledMoved = 0;

  const childrenOf = (idx) => {
    const base = indentOf(list[idx]);
    const stop = subtreeEnd(list, idx);
    const kids = [];
    for (let j = idx + 1; j < stop; j++) if (indentOf(list[j]) === base + 1) kids.push(j);
    return kids;
  };

  const walk = (idx, fromIso, toIso) => {
    const kids = childrenOf(idx);
    if (!kids.length) return;                 // a leaf keeps the span it was given
    const days = workingDays(fromIso, toIso, holidays);
    if (days.length < kids.length) {
      // THE SHARP CASE: five children and three working days. Each child
      // needs at least one day, so this node is left exactly as it is and
      // SAID OUT LOUD - a silent skip here would look like the button
      // half-working.
      refused.push({
        key: list[idx].key,
        name: list[idx].name || "",
        workingDays: days.length,
        children: kids.length,
      });
      return;
    }
    const per = Math.floor(days.length / kids.length);
    let extra = days.length % kids.length;    // the earliest children absorb it
    let cursor = 0;
    kids.forEach((kidIdx) => {
      const take = per + (extra > 0 ? 1 : 0);
      if (extra > 0) extra -= 1;
      const s = days[cursor];
      const e = days[cursor + take - 1];
      cursor += take;
      const row = list[kidIdx];
      if (row.disabled) disabledMoved += 1;
      changes.push({ key: row.key, name: row.name || "", start: s, end: e, days: take });
      // Each child then divides ITS OWN new span.
      walk(kidIdx, s, e);
    });
  };

  // The span to divide: what the caller asked for, or the row's own dates.
  const fromIso = dayOf(start) || dayOf(list[i].manualStart) || dayOf(list[i].start) || "";
  const toIso = dayOf(end) || dayOf(list[i].manualEnd) || dayOf(list[i].end) || "";
  if (!fromIso || !toIso) {
    return { ok: false, why: "This activity has no start and finish to divide.", changes: [], refused: [] };
  }
  if (fromIso > toIso) {
    return { ok: false, why: "Its finish is before its start, so there is no period to divide.", changes: [], refused: [] };
  }
  if (!childrenOf(i).length) {
    return { ok: false, why: "This activity has no children to divide its period between.", changes: [], refused: [] };
  }

  walk(i, fromIso, toIso);
  return {
    ok: true,
    changes,
    refused,
    // Counted, not inferred, so a preview can say what it is about to do.
    moved: changes.length,
    disabledMoved,
    span: { start: fromIso, end: toIso, workingDays: workingDays(fromIso, toIso, holidays).length },
  };
}

// A GROUP'S OWN FINISH, RESTORED WHEN A SAVED PLAN OPENS (owner, 2026-10-12:
// the SpaceX coordination areas all "ended" on their subareas' sign-off day).
//
// A group's dates roll up from the rows under it unless it is pinned
// OUTWARDS, and the only record of that pin is the saved finish itself: a
// stored finish LATER than every child's IS the pin. The template path
// rebuilt it; opening the saved plan did not, so a coordination area whose
// 3-week final sign-off runs past its signed-off subareas showed - and on
// Save would have written - the subareas' date. Only the finish is restored
// here: the start already loads as a manual date and only ever pins outwards.
export function restoreSummaryEnds(rows) {
  return rows.map((r, i) => {
    if (!hasChildrenAt(rows, i) || !r._loadedEnd) return r;
    const base = indentOf(r);
    let latest = null;
    for (let j = i + 1; j < rows.length && indentOf(rows[j]) > base; j++) {
      const e = rows[j]._loadedEnd;
      if (e && (latest == null || e > latest)) latest = e;
    }
    return latest && r._loadedEnd > latest ? { ...r, manualEnd: r._loadedEnd } : r;
  });
}
